import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Trash2, RotateCcw } from 'lucide-react';
import { clsx } from 'clsx';
import {
  deleteVaultItem,
  listVaultItems,
  restoreVaultItem,
  type VaultResourceKind,
} from '@/api/vault';
import { notifications } from '@/utils/notifications';
import { useAccountRole } from '@/hooks/useAccountRole';
import { SettingsReadOnlyBanner } from '@/components/settings/SettingsReadOnlyBanner';

function kindLabel(kind: VaultResourceKind): string {
  if (kind === 'troves') return 'Bookmarks';
  if (kind === 'notes') return 'Notes';
  return 'Highlighter';
}

function rowTitle(row: Record<string, unknown>): string {
  const t = row.title;
  const url = row.url;
  const text = row.selectedText ?? row.content;
  if (typeof t === 'string' && t.trim()) return t.slice(0, 120);
  if (typeof url === 'string' && url.trim()) return url.slice(0, 120);
  if (typeof text === 'string' && text.trim()) return String(text).slice(0, 120);
  return '—';
}

export function VaultSettings() {
  const { t } = useTranslation('settings');
  const { canManageAccount } = useAccountRole();
  const readOnlyOrg = !canManageAccount;
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [items, setItems] = useState<Awaited<ReturnType<typeof listVaultItems>> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listVaultItems();
      setItems(data);
    } catch {
      notifications.error(t('vault.loadFailed'));
      setItems({ troves: [], notes: [], highlighter: [] });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onRestore = async (kind: VaultResourceKind, id: string) => {
    const key = `${kind}:${id}`;
    setBusyId(key);
    try {
      await restoreVaultItem(kind, id);
      notifications.success(t('vault.restored'));
      await load();
    } catch {
      notifications.error(t('vault.restoreFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (kind: VaultResourceKind, id: string) => {
    const key = `${kind}:${id}`;
    setBusyId(key);
    try {
      await deleteVaultItem(kind, id);
      notifications.success(t('vault.deleted'));
      await load();
    } catch {
      notifications.error(t('vault.deleteFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const total =
    (items?.troves.length ?? 0) + (items?.notes.length ?? 0) + (items?.highlighter.length ?? 0);

  return (
    <div className="max-w-5xl animate-in space-y-6 pb-10 fade-in slide-in-from-bottom-2 duration-500">
      {readOnlyOrg ? <SettingsReadOnlyBanner /> : null}
      <div className="space-y-2">
        <h2 className="text-2xl font-black tracking-tight text-foreground sm:text-3xl">{t('vault.title')}</h2>
        <p className="text-sm font-medium leading-relaxed text-muted-foreground">{t('vault.subtitle')}</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
        </div>
      ) : total === 0 ? (
        <p className="rounded-2xl border border-border bg-card px-5 py-8 text-center text-sm text-muted-foreground">
          {t('vault.empty')}
        </p>
      ) : (
        (['troves', 'notes', 'highlighter'] as VaultResourceKind[]).map((kind) => {
          const rows = items?.[kind] ?? [];
          if (rows.length === 0) return null;
          return (
            <section key={kind} className="space-y-3">
              <h3 className="text-lg font-black text-foreground">{kindLabel(kind)}</h3>
              <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
                {rows.map((row) => {
                  const id = row.id as string;
                  const restoreBy = row.vaultRestoreBy as number | undefined;
                  const busy = busyId === `${kind}:${id}`;
                  return (
                    <li key={id} className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-foreground">{rowTitle(row)}</p>
                        {restoreBy ? (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {t('vault.restoreBy')}{' '}
                            {new Date(restoreBy).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </p>
                        ) : null}
                      </div>
                      {canManageAccount ? (
                      <div className="flex shrink-0 gap-2">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void onRestore(kind, id)}
                          className={clsx(
                            'inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-2 text-xs font-black transition-colors',
                            'hover:border-primary hover:text-primary disabled:opacity-50'
                          )}
                        >
                          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                          {t('vault.restore')}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void onDelete(kind, id)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-red-500/30 px-3 py-2 text-xs font-black text-red-500 transition-colors hover:bg-red-500/10 disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t('vault.deleteForever')}
                        </button>
                      </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}
